// src/features/project-intake/hooks/useProjectIntakeData.ts

import { useState, useEffect } from 'react';
import { customersApi, customerContactsApi, projectRequestsApi } from '../../../services/api';
import { Customer, CustomerContactListItem, ProjectRequestOptions } from '../../../types';

interface UseProjectIntakeDataReturn {
  customers: Customer[];
  customerContacts: CustomerContactListItem[];
  options: ProjectRequestOptions | null;
  loading: boolean;
  error: string | null;
}

// Custom hook to fetch customers, contacts, and project request options
export function useProjectIntakeData(customerId: number | undefined): UseProjectIntakeDataReturn {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerContacts, setCustomerContacts] = useState<CustomerContactListItem[]>([]);
  const [options, setOptions] = useState<ProjectRequestOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load customers and contacts on mount
  useEffect(() => {
    let mounted = true;

    const loadInitialData = async () => {
      try {
        const [cust, contacts] = await Promise.all([
          customersApi.getAll({ status: 'active', per_page: 1000 }),
          customerContactsApi.getAll(),
        ]);

        if (!mounted) return;

        setCustomers(cust.data);
        setCustomerContacts(contacts.data);
      } catch (e: any) {
        if (mounted) {
          setError(e?.message ?? 'Failed to load customers');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, []);

  // Load options when customer changes
  useEffect(() => {
    let mounted = true;

    const loadOptions = async () => {
      try {
        const opts = await projectRequestsApi.options(customerId);
        if (!mounted) return;
        setOptions(opts);
      } catch (e: any) {
        if (mounted) {
          setError(e?.message ?? 'Failed to load options');
        }
      }
    };

    loadOptions();

    return () => {
      mounted = false;
    };
  }, [customerId]);

  return {
    customers,
    customerContacts,
    options,
    loading,
    error,
  };
}
